<template>
  <div class="constituent-editor" @focusin="handleFocusIn" @focusout="handleFocusOut">
    <label class="constituents-label heading">CONSTITUENTS IN BLEND</label>

    <div v-if="sortedList.length > 0" class="constituents-list">
      <ConstituentItem
        v-for="(constituent, idx) in sortedList"
        :key="constituent._ui_id"
        :data-uid="constituent.data._uid"
        :constituent="constituent.data"
        :index="idx"
        :total-items="sortedList.length"
        :blend-index="blendIndex"
        :start-expanded="idx === justAddedConstituentIndex"
        :expanded="isExpanded(constituent.data._uid)"
        :active="isActive(constituent.data._uid)"
        :editing="isEditing(constituent.data._uid)"
        @update="handleUpdate(constituent._ui_id, $event)"
        @removed="removeByUid(constituent._ui_id)"
        @save="triggerSave"
        @toggle="handleToggle(constituent.data._uid)"
      />
    </div>

    <p v-else class="has-text-grey is-size-7 mb-2">No constituents added yet.</p>

    <button
      type="button"
      class="button is-small"
      :class="[addButtonVariant, { 'is-fullwidth': addButtonFullWidth }]"
      @click="add"
    >
      <span class="icon"><i class="fas fa-plus"></i></span>
      <span>{{ constituents.length > 0 ? "Add Another Constituent" : "Add Constituent" }}</span>
    </button>
  </div>
</template>

<script setup>
  import { ref, watch, nextTick } from "vue";
  import ConstituentItem from "./blend-constituent-item.vue";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import { normalizeToMcg } from "./utils/sorting";
  import { createWeakMapUidGenerator } from "./utils/uid-generator";
  import { useFocusAwareSort } from "./composables/use-focus-aware-sort";
  import { useExpandCollapse } from "./composables/use-expand-collapse";
  import { Factories } from "./utils/factories";

  const props = defineProps({
    constituents: { type: Array, required: true },
    addButtonVariant: { type: String, default: "is-ghost" },
    addButtonFullWidth: { type: Boolean, default: false },
    blendIndex: { type: Number, default: -1 },
  });

  const store = useSupplementLabelStore();

  const emit = defineEmits(["update:constituents"]);

  const localList = ref([]);
  const justAddedConstituentIndex = ref(null);

  const getUiId = createWeakMapUidGenerator("c_");

  watch(
    () => props.constituents,
    (newVal) => {
      localList.value = newVal.map((c) => ({
        data: c,
        _ui_id: getUiId(c),
      }));
    },
    { immediate: true, deep: true }
  );

  const {
    sortedItems: sortedList,
    handleFocusIn: handleFocusInSort,
    handleFocusOut: handleSortFocusOut,
  } = useFocusAwareSort({
    items: () => localList.value,
    sortFn: (items) => {
      return [...items].sort((a, b) => {
        const aVal = normalizeToMcg(a.data.amount, a.data.unit);
        const bVal = normalizeToMcg(b.data.amount, b.data.unit);
        return bVal - aVal;
      });
    },
    uidKey: "_ui_id",
  });

  const {
    isExpanded,
    isActive,
    isEditing,
    toggle: handleToggle,
    handleFocusIn: handleFocusInExpand,
    handleFocusOut: handleFocusOutExpand,
  } = useExpandCollapse({
    items: () => localList.value.map((w) => w.data),
    justAddedIndex: () => justAddedConstituentIndex.value,
    dataAttr: "data-uid",
  });

  function handleFocusIn(event) {
    // Stop propagation so parent blend card doesn't also get focus highlight
    event.stopPropagation();
    handleFocusInExpand(event);
    handleFocusInSort(event);
  }

  function handleFocusOut(event) {
    event.stopPropagation();
    handleFocusOutExpand(event);
    handleSortFocusOut(event);
  }

  function findWrapperByUid(uid) {
    return localList.value.find((w) => w._ui_id === uid);
  }

  function handleUpdate(uid, event) {
    const wrapper = findWrapperByUid(uid);
    if (!wrapper || !wrapper.data) return;

    if (event.field) {
      wrapper.data[event.field] = event.value;
      if (event.shouldEmit !== false) {
        emit(
          "update:constituents",
          localList.value.map((w) => w.data)
        );
      }
    } else if (event.updates) {
      Object.assign(wrapper.data, event.updates);
      emit(
        "update:constituents",
        localList.value.map((w) => w.data)
      );
    }
  }

  function triggerSave() {
    emit(
      "update:constituents",
      localList.value.map((w) => w.data)
    );
  }

  function add() {
    const newItem = {
      ...Factories.createConstituent(),
      _uid: store._generateUid(),
    };
    localList.value = [...localList.value, { data: newItem, _ui_id: getUiId(newItem) }];
    justAddedConstituentIndex.value = localList.value.length - 1;
    emit(
      "update:constituents",
      localList.value.map((w) => w.data)
    );
    nextTick(() => {
      justAddedConstituentIndex.value = null;
    });
  }

  function removeByUid(uid) {
    localList.value = localList.value.filter((w) => w._ui_id !== uid);
    emit(
      "update:constituents",
      localList.value.map((w) => w.data)
    );
  }
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_mixins";

  .constituent-editor {
    background-color: $bg-light-gray;
    border-radius: 6px;
    padding: 0.75rem 0.75rem 0.25rem;
    border: 1px solid $border-standard;
  }

  .constituents-label {
    display: block;
    margin-bottom: 0.5rem;
    margin-left: 1.5px;
    margin-top: 1px;
    padding-bottom: 1px;
    letter-spacing: 0.125em;
    font-weight: 700;
    color: $label-text-color;
  }

  .constituents-list {
    margin-bottom: 0.5rem;
  }
</style>
